import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Search, CheckCircle2, X, Plus, AlertTriangle } from 'lucide-react';
import {
  describeDefaults,
  normalizeCategorieId,
} from '../config/commerce-categories';
import {
  resolveSelectedCategoryIds,
  buildCategoryChangePayload,
  selectedIdsOutsideFamily,
} from '../utils/categorySelection';
import { getDefaultsFromMetiers } from '../utils/taxonomyDefaults';
import useCommerceTaxonomy from '../hooks/useCommerceTaxonomy';
import useCurrentUser from '../hooks/useCurrentUser';
import FamilyFormModal from './taxonomy/FamilyFormModal';
import MetierFormModal from './taxonomy/MetierFormModal';
import CommerceMetierBadge from './CommerceMetierBadge';

/**
 * Sélecteur multi-métiers (famille → métiers).
 * Source API taxonomie ; fallback catalogue statique si indisponible.
 */
export default function CategoryPicker({
  value,
  values,
  onChange,
  disabled = false,
  showRecap = true,
}) {
  const { isSuperAdmin } = useCurrentUser();
  const taxonomy = useCommerceTaxonomy();
  const {
    families,
    metiers,
    source,
    loading,
    getMetier,
    getFamily,
    searchMetiers,
    refresh,
    createFamily,
    createMetier,
  } = taxonomy;

  const selectedIds = useMemo(
    () => resolveSelectedCategoryIds(value, values),
    [value, values],
  );

  const primaryId = normalizeCategorieId(value) || selectedIds[0] || '';
  const selectedMetier = primaryId ? getMetier(primaryId) : null;
  const [familyId, setFamilyId] = useState(selectedMetier?.familyId || selectedMetier?.family || '');
  const [search, setSearch] = useState('');
  const [familySwitchPrompt, setFamilySwitchPrompt] = useState(null);
  const [familyModalOpen, setFamilyModalOpen] = useState(false);
  const [metierModalOpen, setMetierModalOpen] = useState(false);
  const [modalBusy, setModalBusy] = useState(false);

  const pickerOptions = useMemo(
    () => ({ metiers, getMetierFn: getMetier }),
    [metiers, getMetier],
  );

  useEffect(() => {
    const fam = selectedMetier?.familyId || selectedMetier?.family;
    if (fam && !familyId) {
      setFamilyId(fam);
    }
  }, [primaryId, selectedMetier?.familyId, selectedMetier?.family, familyId]);

  const metiersInFamily = useMemo(() => {
    if (!familyId) return [];
    const list = searchMetiers(search, familyId);
    if (source === 'api') {
      return list.filter((m) => m.actif !== false && !m.archivedAt && !m.deprecated);
    }
    return list;
  }, [search, familyId, searchMetiers, source]);

  const family = familyId ? getFamily(familyId) : null;

  const emitChange = useCallback(
    (nextIds, nextPrimary) => {
      onChange(buildCategoryChangePayload(nextIds, nextPrimary, pickerOptions));
    },
    [onChange, pickerOptions],
  );

  const toggleMetier = (metier) => {
    if (disabled || !metier) return;
    const id = normalizeCategorieId(metier.id);
    const isSelected = selectedIds.includes(id);
    let next;
    let nextPrimary = primaryId;
    if (isSelected) {
      next = selectedIds.filter((x) => x !== id);
      if (nextPrimary === id) nextPrimary = next[0] || '';
    } else {
      next = [...selectedIds, id];
      if (!nextPrimary) nextPrimary = id;
    }
    emitChange(next, nextPrimary);
  };

  const removeMetier = (metierId) => {
    if (disabled) return;
    const id = normalizeCategorieId(metierId);
    const next = selectedIds.filter((x) => x !== id);
    const nextPrimary = primaryId === id ? (next[0] || '') : primaryId;
    emitChange(next, nextPrimary);
  };

  const setPrimary = (metierId) => {
    if (disabled) return;
    const id = normalizeCategorieId(metierId);
    if (!selectedIds.includes(id)) return;
    emitChange(selectedIds, id);
  };

  const applyFamilySwitch = (targetId, { clearOutside }) => {
    setFamilyId(targetId);
    setSearch('');
    setFamilySwitchPrompt(null);
    if (clearOutside) {
      const kept = selectedIds.filter((id) => {
        const m = getMetier(id);
        return (m?.familyId || m?.family) === targetId;
      });
      const nextPrimary = kept.includes(primaryId) ? primaryId : (kept[0] || '');
      emitChange(kept, nextPrimary);
    }
  };

  const selectFamily = (id) => {
    if (disabled) return;
    if (id === familyId) return;
    const outside = selectedIdsOutsideFamily(selectedIds, id, getMetier);
    if (outside.length > 0) {
      setFamilySwitchPrompt({ targetFamilyId: id, outsideIds: outside });
      return;
    }
    setFamilyId(id);
    setSearch('');
  };

  const familyHasSelection = (fId) =>
    selectedIds.some((sid) => {
      const m = getMetier(sid);
      return (m?.familyId || m?.family) === fId;
    });

  const outsideCurrentFamily = familyId
    ? selectedIdsOutsideFamily(selectedIds, familyId, getMetier)
    : [];

  const recapDefaults = useMemo(
    () => getDefaultsFromMetiers(metiers, selectedIds),
    [metiers, selectedIds],
  );

  const canWrite = isSuperAdmin && source === 'api' && !disabled;

  const handleCreateFamily = async (body) => {
    setModalBusy(true);
    try {
      const created = await createFamily(body);
      await refresh();
      if (created?.id) setFamilyId(created.id);
      setFamilyModalOpen(false);
    } finally {
      setModalBusy(false);
    }
  };

  const handleCreateMetier = async (body) => {
    setModalBusy(true);
    try {
      const created = await createMetier(body);
      await refresh();
      if (created?.id) {
        const id = normalizeCategorieId(created.id);
        const next = selectedIds.includes(id) ? selectedIds : [...selectedIds, id];
        // Création rapide : le nouveau métier est sélectionné et devient principal.
        emitChange(next, id);
        setFamilyId(created.familyId || body.familyId);
      }
      setMetierModalOpen(false);
    } finally {
      setModalBusy(false);
    }
  };

  const displayFamilies = source === 'api' ? families.filter((f) => f.actif !== false && !f.archivedAt) : families;

  return (
    <div className="space-y-4">
      {source === 'fallback' && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>Taxonomie indisponible — mode catalogue local</span>
        </div>
      )}

      {loading && (
        <p className="text-xs text-gray-500">Chargement taxonomie…</p>
      )}

      <div>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Type d&apos;activité *
          </label>
          {canWrite && (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setFamilyModalOpen(true)}
                className="inline-flex items-center gap-1 text-xs font-medium text-indigo-700 hover:text-indigo-900"
              >
                <Plus className="h-3 w-3" /> Créer une famille
              </button>
              <button
                type="button"
                onClick={() => setMetierModalOpen(true)}
                className="inline-flex items-center gap-1 text-xs font-medium text-indigo-700 hover:text-indigo-900"
              >
                <Plus className="h-3 w-3" /> Créer un métier
              </button>
            </div>
          )}
        </div>
        <p className="text-xs text-gray-500 mb-3">
          Cochez un ou plusieurs métiers (ex. épicerie fine + restauration). Le métier principal
          sert à préconfigurer les modules ; tous les métiers cochés apparaissent dans les onglets
          du site public. Aucun métier n&apos;est présélectionné.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          {displayFamilies.map((f) => {
            const active = familyId === f.id;
            const hasSel = familyHasSelection(f.id);
            return (
              <button
                key={f.id}
                type="button"
                disabled={disabled}
                onClick={() => selectFamily(f.id)}
                className={`rounded-xl border p-3 text-left transition ${
                  active
                    ? 'border-indigo-600 bg-indigo-50 ring-2 ring-indigo-200'
                    : hasSel
                      ? 'border-indigo-300 bg-indigo-50/40'
                      : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
                } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                <span className="text-xl block mb-1">{f.icon}</span>
                <span className="text-sm font-medium text-gray-900 leading-tight block">{f.label}</span>
                <span className="text-xs text-gray-500 mt-1 block line-clamp-2">{f.hint}</span>
              </button>
            );
          })}
        </div>
      </div>

      {familySwitchPrompt && (
        <div
          className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm"
          role="dialog"
          aria-label="Changement de famille"
        >
          <p className="font-medium text-amber-900 mb-1">
            Métiers d&apos;autres familles déjà sélectionnés
          </p>
          <p className="text-xs text-amber-800 mb-3">
            {familySwitchPrompt.outsideIds.length} métier
            {familySwitchPrompt.outsideIds.length > 1 ? 's' : ''} resterai
            {familySwitchPrompt.outsideIds.length > 1 ? 'ent' : 't'} sélectionné
            {familySwitchPrompt.outsideIds.length > 1 ? 's' : ''} si vous changez de famille.
            Choisissez de les conserver (multi-familles) ou de les retirer.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={disabled}
              onClick={() =>
                applyFamilySwitch(familySwitchPrompt.targetFamilyId, { clearOutside: false })
              }
              className="px-3 py-1.5 text-xs font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700"
            >
              Conserver (multi-familles)
            </button>
            <button
              type="button"
              disabled={disabled}
              onClick={() =>
                applyFamilySwitch(familySwitchPrompt.targetFamilyId, { clearOutside: true })
              }
              className="px-3 py-1.5 text-xs font-medium rounded-md border border-amber-300 bg-white text-amber-900 hover:bg-amber-100"
            >
              Retirer les métiers hors famille
            </button>
            <button
              type="button"
              disabled={disabled}
              onClick={() => setFamilySwitchPrompt(null)}
              className="px-3 py-1.5 text-xs font-medium rounded-md text-gray-600 hover:bg-gray-100"
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
        <p className="text-xs font-medium text-gray-700 mb-2">
          Métiers sélectionnés ({selectedIds.length})
          {outsideCurrentFamily.length > 0 && familyId
            ? ` · dont ${outsideCurrentFamily.length} hors famille affichée`
            : ''}
        </p>
        {selectedIds.length === 0 ? (
          <p className="text-xs text-gray-500">Aucun métier sélectionné</p>
        ) : (
          <ul className="flex flex-wrap gap-2">
            {selectedIds.map((id) => {
              const m = getMetier(id);
              const isPrimary = id === primaryId;
              return (
                <li key={id} className="inline-flex items-center gap-1">
                  <CommerceMetierBadge
                    code={id}
                    metier={m}
                    getMetier={getMetier}
                    primary={isPrimary}
                  />
                  {!isPrimary && (
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={() => setPrimary(id)}
                      className="text-[10px] text-indigo-700 underline disabled:opacity-50"
                    >
                      Principal
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => removeMetier(id)}
                    className="p-0.5 rounded hover:bg-gray-200 disabled:opacity-50"
                    aria-label={`Retirer ${m?.label || id}`}
                    title="Retirer"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {family && (
        <div>
          <div className="flex items-center justify-between gap-2 mb-2">
            <label className="text-sm font-medium text-gray-700">
              Métier — {family.icon} {family.label}
            </label>
            <span className="text-xs text-gray-500">
              {selectedIds.length} sélectionné{selectedIds.length > 1 ? 's' : ''}
            </span>
          </div>
          <div className="relative mb-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher un type d'activité…"
              disabled={disabled}
              className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500"
            />
          </div>
          <div className="max-h-48 overflow-y-auto rounded-lg border border-gray-200 divide-y divide-gray-100">
            {metiersInFamily.length === 0 ? (
              <p className="p-4 text-sm text-gray-500 text-center">Aucun métier trouvé</p>
            ) : (
              metiersInFamily.map((m) => {
                const active = selectedIds.includes(m.id);
                const isPrimary = primaryId === m.id;
                return (
                  <div
                    key={m.id}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 ${
                      active ? 'bg-indigo-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={() => toggleMetier(m)}
                      className="flex flex-1 items-center gap-3 text-left min-w-0"
                    >
                      <span className="text-lg">{m.icon || '📌'}</span>
                      <span className="flex-1 min-w-0">
                        <span className="font-medium text-gray-900 text-sm block">{m.label}</span>
                        {m.description && (
                          <span className="text-xs text-gray-500 block truncate">{m.description}</span>
                        )}
                      </span>
                      {m.badge && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 shrink-0">
                          {m.badge}
                        </span>
                      )}
                      {active && <CheckCircle2 className="h-4 w-4 text-indigo-600 shrink-0" />}
                    </button>
                    {active && (
                      <button
                        type="button"
                        disabled={disabled || isPrimary}
                        onClick={() => setPrimary(m.id)}
                        className={`text-[10px] px-2 py-1 rounded-full shrink-0 border ${
                          isPrimary
                            ? 'bg-indigo-600 text-white border-indigo-600'
                            : 'bg-white text-indigo-700 border-indigo-200 hover:bg-indigo-50'
                        }`}
                        title="Métier principal (préconfig modules)"
                      >
                        {isPrimary ? 'Principal' : 'Définir principal'}
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {!family && (
        <p className="text-sm text-gray-500">
          Choisissez une famille d&apos;activité pour afficher les métiers.
        </p>
      )}

      {showRecap && selectedIds.length > 0 && (
        <div className="rounded-lg border border-indigo-100 bg-indigo-50/60 px-4 py-3 text-sm">
          <p className="text-xs text-indigo-800 mb-2">Modules activés à la création (union) :</p>
          <ul className="text-xs text-indigo-900 list-disc list-inside space-y-0.5">
            {describeDefaults(recapDefaults).map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>
      )}

      {/* stopPropagation : le submit métier/famille ne doit pas remonter au form commerçant. */}
      <div
        onSubmit={(e) => {
          e.stopPropagation();
        }}
      >
        <FamilyFormModal
          open={familyModalOpen}
          onClose={() => setFamilyModalOpen(false)}
          onSubmit={handleCreateFamily}
          submitting={modalBusy}
          variant="quick"
          existingFamilyIds={families.map((f) => f.id)}
        />
        <MetierFormModal
          open={metierModalOpen}
          onClose={() => setMetierModalOpen(false)}
          onSubmit={handleCreateMetier}
          families={families}
          defaultFamilyId={familyId}
          submitting={modalBusy}
          variant="quick"
          existingMetierIds={metiers.map((m) => m.id)}
        />
      </div>
    </div>
  );
}
